"use client";

import {
  Button,
  cn,
  FormNumber,
  FormRoot,
  FormTextarea,
  Input,
  Label,
  Loader,
  RHF,
  toast,
  zodResolver,
} from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon, InfoIcon, LockIcon, SearchIcon } from "lucide-react";
import { useMemo, useState } from "react";
import type { Resolver } from "react-hook-form";
import { z } from "zod";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import {
  canUseMerchantPermission,
  type ShopBranchOption,
  type ShopSellableCatalogRow,
  type ShopVerifiedLevel,
  shopCatalogApi,
  shopInventoryApi,
} from "@/modules/merchant/presentation/api/client";
import {
  useReleaseShopInventoryMutation,
  useReserveShopInventoryMutation,
} from "@/modules/merchant/presentation/api/queries";
import { shopBreadcrumbs } from "@/modules/merchant/presentation/lib/shop-breadcrumb";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { RequireMerchantPermission } from "@/modules/merchant/presentation/ui/RequireMerchantPermission";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";

const INVENTORY_ROUTE = "/app/shops/$shopId/inventory" as const;

const ReserveFormSchema = z.object({
  branchId: z.string().uuid(),
  shopProductSkuId: z.string().uuid(),
  qty: z.coerce.number().int().positive().max(999_999),
  note: z.string().max(2000).optional(),
});

type ReserveFormValues = z.infer<typeof ReserveFormSchema>;

const ReleaseFormSchema = z.object({
  branchId: z.string().uuid(),
  shopProductSkuId: z.string().uuid(),
  qty: z.coerce.number().int().positive().max(999_999),
  reference: z
    .string()
    .max(2000)
    .optional()
    .transform((v) => {
      if (v === undefined || v === "") return undefined;
      const t = v.trim();
      return t === "" ? undefined : t;
    }),
});

type ReleaseFormValues = z.infer<typeof ReleaseFormSchema>;

type SessionLogAction = "reserved" | "released";

type SessionLogRow = {
  id: string;
  at: Date;
  action: SessionLogAction;
  productLine: string;
  branchLine: string;
  qty: number;
};

function CardShell({
  accentClass,
  title,
  subtitle,
  children,
}: {
  accentClass: string;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section
      className={cn(
        "flex flex-col rounded-xl border border-border bg-card p-4 shadow-sm sm:p-6",
        "border-t-4 pt-5",
        accentClass,
      )}
    >
      <div className="mb-4 space-y-1">
        <h2 className="font-heading font-semibold text-card-foreground text-sm uppercase tracking-[0.12em]">
          {title}
        </h2>
        {subtitle ? (
          <p className="text-muted-foreground text-xs leading-relaxed">
            {subtitle}
          </p>
        ) : null}
      </div>
      {children}
    </section>
  );
}

async function resolveLogLabels(
  shopId: string,
  branchId: string,
  shopProductSkuId: string,
): Promise<{ branchLine: string; productLine: string }> {
  const [branch, sku] = await Promise.all([
    shopInventoryApi.getBranch(shopId, branchId),
    shopCatalogApi.getSellableSku(shopId, shopProductSkuId),
  ]);
  return {
    branchLine: branch?.name ?? branchId.slice(0, 8),
    productLine: sku
      ? `${sku.productName} · ${sku.shopSku}`
      : shopProductSkuId.slice(0, 8),
  };
}

export function ShopInventoryReserveReleasePage() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const nav = useNavigate();
  const { currentPermissions, currentShop } = useMerchantShops(shopId);
  const verifiedLevel: ShopVerifiedLevel | null =
    currentShop?.verifiedLevel ?? null;

  const canAdjust = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.inventory.adjust,
    verifiedLevel,
  );

  const reserveMut = useReserveShopInventoryMutation(shopId);
  const releaseMut = useReleaseShopInventoryMutation(shopId);

  const [sessionLogs, setSessionLogs] = useState<SessionLogRow[]>([]);

  const reserveForm = RHF.useForm<ReserveFormValues>({
    resolver: zodResolver(ReserveFormSchema) as Resolver<ReserveFormValues>,
    defaultValues: {
      branchId: "",
      shopProductSkuId: "",
      qty: 1,
      note: "",
    },
  });

  const releaseForm = RHF.useForm<ReleaseFormValues>({
    resolver: zodResolver(ReleaseFormSchema) as Resolver<ReleaseFormValues>,
    defaultValues: {
      branchId: "",
      shopProductSkuId: "",
      qty: 1,
      reference: "",
    },
  });

  const branchQueryKey = useMemo((): string[] => {
    return ["shops", shopId ?? "", "inventory-reserve", "branch"];
  }, [shopId]);

  const skuQueryKey = useMemo((): string[] => {
    return ["shops", shopId ?? "", "inventory-reserve", "sku"];
  }, [shopId]);

  const timeFmt = useMemo(
    () =>
      new Intl.DateTimeFormat("lo-LA", {
        dateStyle: "short",
        timeStyle: "short",
      }),
    [],
  );

  const pushLog = (row: Omit<SessionLogRow, "id">) => {
    setSessionLogs((prev) =>
      [{ id: crypto.randomUUID(), ...row }, ...prev].slice(0, 25),
    );
  };

  return (
    <RequireMerchantPermission permission={MerchantPermissions.inventory.read}>
      <Header>
        <Breadcrumb items={shopBreadcrumbs.inventoryReserveRelease(shopId)} />
      </Header>
      <Main className="bg-muted/35">
        <div className="mx-auto max-w-6xl space-y-8">
          <div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex-col flex-wrap items-start gap-3">
                <h1 className="font-heading font-semibold text-2xl text-foreground tracking-tight">
                  ຈອງ ແລະ ປົດຈອງ
                </h1>
                <p className="mt-2 max-w-2xl text-muted-foreground text-sm">
                  ຈອງຍອດພ້ອມຂາຍ (reserved) ຫຼື ປົດຈອງຄືນ — ບັນທຶກ movement{" "}
                  <span className="font-mono text-[11px]">reserve</span> /{" "}
                  <span className="font-mono text-[11px]">release</span>.
                </p>
              </div>

              <Button
                variant="ghost"
                size="sm"
                className="mb-3 -ml-2 gap-1"
                type="button"
                onClick={() => {
                  if (!shopId) return;
                  nav({ to: INVENTORY_ROUTE, params: { shopId } });
                }}
              >
                <ArrowLeftIcon className="size-4" aria-hidden />
                ກັບສາງ
              </Button>
            </div>
          </div>

          <div
            className="flex gap-3 rounded-xl border border-secondary/25 bg-secondary/10 px-4 py-3 text-sm"
            role="note"
          >
            <InfoIcon
              className="mt-0.5 size-5 shrink-0 text-secondary"
              aria-hidden
            />
            <p className="text-foreground leading-relaxed">
              <span className="font-medium">ໝາຍເຫດລະບົບ:</span>{" "}
              ການຈອງໃນລະບົບນີ້ບໍ່ມີເວລາໝົດອັດຕະໂນມັດ — ເມື່ອມີການຜູກ hold order ຫຼື policy TTL
              ຈະສະແດງທີ່ນີ້. ປັດຈຸບັນກວດ{" "}
              <span className="font-mono text-xs">on_hand - reserved</span>{" "}
              ກ່ອນຂາຍທີ່ POS.
            </p>
          </div>

          {canAdjust ? (
            <>
              <div className="grid gap-6 lg:grid-cols-2">
                <CardShell
                  accentClass="border-t-primary"
                  title="ຈອງສະຕ໋ອກ"
                  subtitle="ເພີ່ມ reserved — ບໍ່ຫຼຸດ on_hand"
                >
                  <FormRoot<ReserveFormValues>
                    methods={reserveForm}
                    onSubmit={async (v) => {
                      if (!shopId) return;
                      try {
                        await reserveMut.mutateAsync({
                          branchId: v.branchId,
                          shopProductSkuId: v.shopProductSkuId,
                          qty: v.qty,
                          note: v.note?.trim() || undefined,
                        });
                        const labels = await resolveLogLabels(
                          shopId,
                          v.branchId,
                          v.shopProductSkuId,
                        );
                        pushLog({
                          at: new Date(),
                          action: "reserved",
                          ...labels,
                          qty: v.qty,
                        });
                        reserveForm.reset({
                          branchId: v.branchId,
                          shopProductSkuId: "",
                          qty: 1,
                          note: "",
                        });
                      } catch {
                        /* toast ຢູ່ mutation */
                      }
                    }}
                    className="space-y-3"
                  >
                    <div className="space-y-2">
                      <Label className="text-muted-foreground text-xs uppercase tracking-wide">
                        ສາຂາ
                      </Label>
                      {shopId ? (
                        <InfiniteCombobox<ShopBranchOption>
                          queryKey={[...branchQueryKey, "reserve"]}
                          queryFn={async ({ search: q, pageParam }) => {
                            if (!shopId) {
                              return { items: [], nextPage: null };
                            }
                            return shopInventoryApi.lookupBranches(shopId, {
                              page: pageParam,
                              search: q.trim() || undefined,
                              limit: 20,
                            });
                          }}
                          preloadQueryFn={(id) =>
                            shopId
                              ? shopInventoryApi.getBranch(shopId, id)
                              : Promise.resolve(null)
                          }
                          getLabel={(item) => item.name}
                          getValue={(item) => item.id}
                          value={reserveForm.watch("branchId")}
                          onValueChange={(id) =>
                            reserveForm.setValue("branchId", id, {
                              shouldValidate: true,
                            })
                          }
                          placeholder="ເລືອກສາຂາ..."
                          className="h-10 w-full bg-muted/50"
                          clearable
                        />
                      ) : null}
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-1.5 text-muted-foreground text-xs uppercase tracking-wide">
                        <SearchIcon
                          className="size-3.5 text-secondary"
                          aria-hidden
                        />
                        ສິນຄ້າ / SKU
                      </Label>
                      {shopId ? (
                        <InfiniteCombobox<ShopSellableCatalogRow>
                          queryKey={[...skuQueryKey, "reserve"]}
                          queryFn={async ({ search: q, pageParam }) => {
                            if (!shopId) {
                              return { items: [], nextPage: null };
                            }
                            const limit = 20;
                            const page = pageParam ?? 1;
                            const offset = (page - 1) * limit;
                            const res = await shopCatalogApi.listSellable(
                              shopId,
                              {
                                offset,
                                limit,
                                q: q.trim() || undefined,
                                barcode: undefined,
                              },
                            );
                            const next =
                              offset + res.data.length < res.meta.total
                                ? page + 1
                                : null;
                            // Reserve/release stock only — services (labor) have no
                            // inventory, so exclude them from the picker.
                            return {
                              items: res.data.filter(
                                (r) => r.productType !== "service",
                              ),
                              nextPage: next,
                            };
                          }}
                          preloadQueryFn={(id) =>
                            shopId
                              ? shopCatalogApi.getSellableSku(shopId, id)
                              : Promise.resolve(null)
                          }
                          getLabel={(item) =>
                            `${item.productName} · ${item.shopSku}`
                          }
                          getValue={(item) => item.id}
                          value={reserveForm.watch("shopProductSkuId")}
                          onValueChange={(id) =>
                            reserveForm.setValue("shopProductSkuId", id, {
                              shouldValidate: true,
                            })
                          }
                          placeholder="ຄົ້ນຫາ SKU..."
                          className="h-10 w-full bg-muted/50"
                          clearable
                        />
                      ) : null}
                    </div>
                    <FormNumber
                      name="qty"
                      label="ຈຳນວນທີ່ຈອງ"
                      min={1}
                      className="h-10"
                    />
                    <FormTextarea
                      name="note"
                      rows={2}
                      label="ໝາຍເຫດພາຍໃນ"
                      placeholder="ເຫດຜົນການຈອງ..."
                      className="bg-muted/50"
                    />
                    <Button
                      type="submit"
                      className="h-11 w-full gap-2 font-semibold"
                      disabled={reserveMut.isPending}
                    >
                      {reserveMut.isPending ? (
                        <Loader />
                      ) : (
                        <LockIcon className="size-4" aria-hidden />
                      )}
                      ຈອງຈຳນວນ
                    </Button>
                  </FormRoot>
                </CardShell>

                <CardShell
                  accentClass="border-t-secondary"
                  title="ປົດຈອງສະຕ໋ອກ"
                  subtitle="ຫຼຸດ reserved — ບໍ່ແຕະ on_hand"
                >
                  <FormRoot<ReleaseFormValues>
                    methods={releaseForm}
                    onSubmit={async (v) => {
                      if (!shopId) return;
                      try {
                        await releaseMut.mutateAsync({
                          branchId: v.branchId,
                          shopProductSkuId: v.shopProductSkuId,
                          qty: v.qty,
                          note: v.reference,
                        });
                        const labels = await resolveLogLabels(
                          shopId,
                          v.branchId,
                          v.shopProductSkuId,
                        );
                        pushLog({
                          at: new Date(),
                          action: "released",
                          ...labels,
                          qty: v.qty,
                        });
                        releaseForm.reset({
                          branchId: v.branchId,
                          shopProductSkuId: "",
                          qty: 1,
                          reference: "",
                        });
                      } catch {
                        /* toast ຢູ່ mutation */
                      }
                    }}
                    className="space-y-3"
                  >
                    <div className="space-y-2">
                      <Label className="text-muted-foreground text-xs uppercase tracking-wide">
                        ສາຂາ
                      </Label>
                      {shopId ? (
                        <InfiniteCombobox<ShopBranchOption>
                          queryKey={[...branchQueryKey, "release"]}
                          queryFn={async ({ search: q, pageParam }) => {
                            if (!shopId) {
                              return { items: [], nextPage: null };
                            }
                            return shopInventoryApi.lookupBranches(shopId, {
                              page: pageParam,
                              search: q.trim() || undefined,
                              limit: 20,
                            });
                          }}
                          preloadQueryFn={(id) =>
                            shopId
                              ? shopInventoryApi.getBranch(shopId, id)
                              : Promise.resolve(null)
                          }
                          getLabel={(item) => item.name}
                          getValue={(item) => item.id}
                          value={releaseForm.watch("branchId")}
                          onValueChange={(id) =>
                            releaseForm.setValue("branchId", id, {
                              shouldValidate: true,
                            })
                          }
                          placeholder="ເລືອກສາຂາ..."
                          className="h-10 w-full bg-muted/50"
                          clearable
                        />
                      ) : null}
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-1.5 text-muted-foreground text-xs uppercase tracking-wide">
                        <SearchIcon
                          className="size-3.5 text-secondary"
                          aria-hidden
                        />
                        ສິນຄ້າ / SKU
                      </Label>
                      {shopId ? (
                        <InfiniteCombobox<ShopSellableCatalogRow>
                          queryKey={[...skuQueryKey, "release"]}
                          queryFn={async ({ search: q, pageParam }) => {
                            if (!shopId) {
                              return { items: [], nextPage: null };
                            }
                            const limit = 20;
                            const page = pageParam ?? 1;
                            const offset = (page - 1) * limit;
                            const res = await shopCatalogApi.listSellable(
                              shopId,
                              {
                                offset,
                                limit,
                                q: q.trim() || undefined,
                                barcode: undefined,
                              },
                            );
                            const next =
                              offset + res.data.length < res.meta.total
                                ? page + 1
                                : null;
                            // Reserve/release stock only — services (labor) have no
                            // inventory, so exclude them from the picker.
                            return {
                              items: res.data.filter(
                                (r) => r.productType !== "service",
                              ),
                              nextPage: next,
                            };
                          }}
                          preloadQueryFn={(id) =>
                            shopId
                              ? shopCatalogApi.getSellableSku(shopId, id)
                              : Promise.resolve(null)
                          }
                          getLabel={(item) =>
                            `${item.productName} · ${item.shopSku}`
                          }
                          getValue={(item) => item.id}
                          value={releaseForm.watch("shopProductSkuId")}
                          onValueChange={(id) =>
                            releaseForm.setValue("shopProductSkuId", id, {
                              shouldValidate: true,
                            })
                          }
                          placeholder="ຄົ້ນຫາ SKU..."
                          className="h-10 w-full bg-muted/50"
                          clearable
                        />
                      ) : null}
                    </div>
                    <FormNumber
                      name="qty"
                      label="ຈຳນວນທີ່ປົດຈອງ"
                      min={1}
                      className="h-10"
                    />
                    <div className="space-y-2">
                      <Label
                        htmlFor="release-reference"
                        className="text-muted-foreground text-xs uppercase tracking-wide"
                      >
                        ອ້າງອີງການປົດຈອງ
                      </Label>
                      <Input
                        id="release-reference"
                        className="h-10 bg-muted/50"
                        placeholder="Order ID ຫຼື ເລກອ້າງອີງໂອນ..."
                        {...releaseForm.register("reference")}
                      />
                    </div>
                    <Button
                      type="submit"
                      variant="outline"
                      className="h-11 w-full gap-2 border-2 border-secondary bg-transparent font-semibold text-secondary hover:bg-secondary/10 hover:text-secondary"
                      disabled={releaseMut.isPending}
                    >
                      {releaseMut.isPending ? (
                        <Loader />
                      ) : (
                        <LockIcon className="size-4" aria-hidden />
                      )}
                      ປົດຈອງຈຳນວນ
                    </Button>
                  </FormRoot>
                </CardShell>
              </div>

              <section className="rounded-xl border border-border bg-card shadow-sm">
                <div className="flex flex-col gap-2 border-border border-b px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                  <h2 className="font-heading font-semibold text-card-foreground text-sm uppercase tracking-[0.12em]">
                    ບັນທຶກຈອງຫຼ້າສຸດ (ຊັ້ນນີ້)
                  </h2>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="shrink-0 gap-1 text-secondary hover:text-secondary"
                    onClick={() =>
                      toast.message(
                        "ປະຫວັດ movement ແບບເຕັມຈະເປີດຈາກ API ໃນ Phase ລາຍງານ/ledger",
                      )
                    }
                  >
                    ເບິ່ງ ledger ເຕັມ
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[640px] text-left text-sm">
                    <thead>
                      <tr className="border-border border-b bg-muted/40 text-muted-foreground text-xs uppercase tracking-wide">
                        <th className="px-4 py-2.5 font-medium sm:px-6">
                          ເວລາ
                        </th>
                        <th className="px-4 py-2.5 font-medium sm:px-6">
                          ການກະທຳ
                        </th>
                        <th className="px-4 py-2.5 font-medium sm:px-6">
                          ສິນຄ້າ / SKU
                        </th>
                        <th className="px-4 py-2.5 font-medium sm:px-6">
                          ສາຂາ
                        </th>
                        <th className="px-4 py-2.5 text-right font-medium sm:px-6">
                          ຈຳນວນ
                        </th>
                        <th className="px-4 py-2.5 font-medium sm:px-6">
                          ຜູ້ດຳເນີນການ
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sessionLogs.length === 0 ? (
                        <tr>
                          <td
                            colSpan={6}
                            className="px-4 py-10 text-center text-muted-foreground sm:px-6"
                          >
                            ຍັງບໍ່ມີບັນທຶກໃນກະບົນນີ້ — ຈອງ ຫຼື ປົດຈອງສຳເລັດແລ້ວຈະສະແດງທີ່ນີ້
                          </td>
                        </tr>
                      ) : (
                        sessionLogs.map((row) => (
                          <tr
                            key={row.id}
                            className="border-border border-b last:border-0"
                          >
                            <td className="whitespace-nowrap px-4 py-3 tabular-nums sm:px-6">
                              {timeFmt.format(row.at)}
                            </td>
                            <td className="px-4 py-3 sm:px-6">
                              <span
                                className={cn(
                                  "inline-flex rounded-full px-2 py-0.5 font-medium text-xs",
                                  row.action === "reserved"
                                    ? "bg-primary/15 text-primary"
                                    : "bg-secondary/15 text-secondary",
                                )}
                              >
                                {row.action === "reserved"
                                  ? "ຈອງແລ້ວ"
                                  : "ປົດຈອງແລ້ວ"}
                              </span>
                            </td>
                            <td className="max-w-[220px] px-4 py-3 sm:max-w-xs sm:px-6">
                              <span className="line-clamp-2 font-medium text-card-foreground">
                                {row.productLine}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-muted-foreground sm:px-6">
                              {row.branchLine}
                            </td>
                            <td className="px-4 py-3 text-right tabular-nums sm:px-6">
                              {row.qty}
                            </td>
                            <td className="px-4 py-3 text-muted-foreground sm:px-6">
                              ທ່ານ
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          ) : (
            <p className="rounded-xl border border-border bg-muted/30 px-4 py-3 text-muted-foreground text-sm">
              ບໍ່ມີສິດ <span className="font-mono text-xs">inventory.adjust</span> —
              ຕິດຕໍ່ຜູ້ຄຸມຮ້ານເພື່ອຈອງ ຫຼື ປົດຈອງ
            </p>
          )}
        </div>
      </Main>
    </RequireMerchantPermission>
  );
}
